import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";
import { createApiCall } from "services";

/** One font a brand has uploaded (VRG-348). */
export interface CompanyFont {
  id: string;
  /** Public URL. This exact string is what the theme stores as its font. */
  file_url: string;
  family_label: string;
  format: "woff2" | "otf" | "ttf";
  byte_size: number;
  rights_confirmed_by_email: string;
  rights_confirmed_at: string;
  rights_wording_version: string;
  created_at: string;
}

/**
 * The rights sentence and upload constraints, served by the API.
 *
 * Fetched rather than hardcoded so the wording a brand SEES and the version
 * RECORDED against their upload cannot drift. A literal in this file would keep
 * rendering the old sentence after the server constant was bumped, and the
 * stored record would then attest to text nobody was shown.
 */
export interface FontRightsWording {
  wording: string;
  version: string;
  accept_extensions: string[];
  max_size_mb: number;
  max_per_company: number;
}

const fontsKey = (companyId?: string) => ["company-fonts", companyId];

export const useCompanyFonts = (
  companyId?: string,
): UseQueryResult<CompanyFont[], Error> =>
  useQuery({
    queryKey: fontsKey(companyId),
    queryFn: async () => {
      const response = await createApiCall({
        method: "GET",
        url: `company-fonts/admin/${companyId}`,
      });
      // Coerce to an array rather than trusting the payload. The shared axios
      // layer resolves some failures instead of rejecting, so a 401/500 body
      // (`{message, code, details}`) can arrive here where a list is expected —
      // which crashed the font picker outright the first time this ran. A
      // consumer of this hook must never have to defend against that.
      const payload: unknown = response?.data;
      if (Array.isArray(payload)) return payload as CompanyFont[];
      const nested = (payload as {data?: unknown})?.data;
      return (Array.isArray(nested) ? nested : []) as CompanyFont[];
    },
    enabled: !!companyId,
  }) as UseQueryResult<CompanyFont[], Error>;

export const useFontRightsWording = (): UseQueryResult<
  FontRightsWording,
  Error
> =>
  useQuery({
    queryKey: ["company-fonts", "rights-wording"],
    queryFn: async () => {
      const response = await createApiCall({
        method: "GET",
        url: "company-fonts/admin/rights-wording",
      });
      return response.data as FontRightsWording;
    },
    staleTime: 1000 * 60 * 60,
  }) as UseQueryResult<FontRightsWording, Error>;

export interface UploadCompanyFontParams {
  companyId: string;
  file: File;
  familyLabel?: string;
  /** The checkbox. The server refuses anything that is not exactly true. */
  rightsConfirmed: boolean;
}

export const useUploadCompanyFont = (): UseMutationResult<
  CompanyFont,
  Error,
  UploadCompanyFontParams
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      companyId,
      file,
      familyLabel,
      rightsConfirmed,
    }: UploadCompanyFontParams) => {
      const formData = new FormData();
      formData.append("file", file);
      // A multipart field is a string on the wire; the server compares against
      // 'true' rather than testing truthiness, so 'false' is a real refusal.
      formData.append("rights_confirmed", rightsConfirmed ? "true" : "false");
      if (familyLabel) formData.append("family_label", familyLabel);

      // Content-Type is deliberately omitted — `createApiCall` detects FormData
      // and lets the browser set the multipart boundary itself.
      const response = await createApiCall({
        method: "POST",
        url: `company-fonts/admin/${companyId}`,
        data: formData,
      });

      // Validate the SHAPE rather than trusting resolution. The shared axios
      // layer resolves some error responses instead of rejecting, so a 403 body
      // (`{message, code, details}`) arrives here looking like a fulfilled
      // promise — which made the modal close on a "successful" upload that had
      // in fact stored nothing. A silent false success is worse than an error,
      // because the user walks away believing the font is there.
      const font = response?.data as CompanyFont | {message?: string} | undefined;
      if (!font || typeof (font as CompanyFont).file_url !== "string") {
        throw new Error(
          (font as {message?: string})?.message ??
            "That font could not be uploaded.",
        );
      }
      return font as CompanyFont;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: fontsKey(variables.companyId) });
    },
  });
};

export const useDeleteCompanyFont = (): UseMutationResult<
  void,
  Error,
  { companyId: string; fontId: string }
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ companyId, fontId }) => {
      await createApiCall({
        method: "DELETE",
        url: `company-fonts/admin/${companyId}/${fontId}`,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: fontsKey(variables.companyId) });
    },
  });
};
